import React from "react";
import { useNavigate } from "react-router-dom";
import { LevelBadge } from "@/components/LevelBadge";
import { PersonalControls } from "@/components/PersonalControls";
import { LikeButton } from "@/components/LikeButton";
import { fileUrl } from "@/components/FileUpload";
import { Youtube, FileText, Heart, Trophy, Lock } from "lucide-react";

export function DanceCard({ dance, onStatusChange, compact = false }) {
  const statuses = dance.my_statuses || [];
  const navigate = useNavigate();
  const go = () => navigate(`/dances/${dance.slug}`);
  const stop = (e) => e.stopPropagation();

  if (dance.locked) {
    return (
      <div data-testid={`dance-card-${dance.slug}`} onClick={go} role="button" tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); go(); } }}
        className="card-hover group flex cursor-pointer flex-col rounded-2xl border border-border bg-card p-5 outline-none focus-visible:ring-2 focus-visible:ring-[#C85A32]">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display text-lg font-bold leading-tight text-[#2D1E18] group-hover:text-[#B04B26]">{dance.name}</h3>
          <Lock size={16} className="mt-1 shrink-0 text-[#B04B26]" data-testid={`dance-lock-${dance.slug}`} />
        </div>
        <div className="mt-3"><LevelBadge level={dance.level} /></div>
      </div>
    );
  }

  return (
    <div data-testid={`dance-card-${dance.slug}`} onClick={go} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); go(); } }}
      className="card-hover group flex cursor-pointer flex-col rounded-2xl border border-border bg-card p-5 outline-none focus-visible:ring-2 focus-visible:ring-[#C85A32]">
      <div className="flex items-start justify-between gap-3">
        <div>
          {dance.is_club_favourite && (
            <span data-testid={`dance-fav-badge-${dance.slug}`} className="mb-1.5 inline-flex items-center gap-1 rounded-full bg-[#FBEBE4] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#B04B26]">
              <Trophy size={11} /> Club favourite
            </span>
          )}
          <h3 className="font-display text-lg font-bold leading-tight text-[#2D1E18] group-hover:text-[#B04B26]">{dance.name}</h3>
          {dance.choreographer && <p className="mt-0.5 text-sm text-[#6E5B51]">by {dance.choreographer}</p>}
        </div>
        {statuses.includes("favourite") && <Heart size={18} className="shrink-0 text-[#B04B26]" fill="currentColor" />}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <LevelBadge level={dance.level} />
        {dance.counts ? <span className="text-xs text-[#6E5B51]">{dance.counts} counts</span> : null}
        {dance.walls ? <span className="text-xs text-[#6E5B51]">· {dance.walls} walls</span> : null}
        <span className="ml-auto" onClick={stop}><LikeButton dance={dance} size="sm" stopNav /></span>
      </div>
      {dance.music && <p className="mt-2 text-sm text-[#5C4A42]"><span className="text-[#6E5B51]">Music:</span> {dance.music}{dance.artist ? ` — ${dance.artist}` : ""}</p>}
      {!compact && (
        <>
          <div className="mt-3 flex gap-2">
            {dance.tutorial_url && <a href={dance.tutorial_url} target="_blank" rel="noopener noreferrer" onClick={stop} data-testid={`dance-tutorial-${dance.slug}`} className="inline-flex items-center gap-1 rounded-lg bg-[#FBEBE4] px-2.5 py-1 text-xs font-semibold text-[#B04B26]"><Youtube size={14} /> Tutorial</a>}
            {(dance.step_sheet_path || dance.step_sheet_url || dance.copperknob_url) && <a href={dance.step_sheet_path ? fileUrl(dance.step_sheet_path) : (dance.step_sheet_url || dance.copperknob_url)} target="_blank" rel="noopener noreferrer" onClick={stop} data-testid={`dance-stepsheet-${dance.slug}`} className="inline-flex items-center gap-1 rounded-lg bg-secondary px-2.5 py-1 text-xs font-semibold text-[#5C4A42]"><FileText size={14} /> Step sheet</a>}
          </div>
          <div className="mt-4 border-t border-border pt-3" onClick={stop}>
            <PersonalControls dance={dance} statuses={statuses} onChange={(n) => onStatusChange?.(dance.id, n)} size="sm" />
          </div>
        </>
      )}
    </div>
  );
}
